import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cursorArraste } from '../input/cursor';

/*
  Faixa que rola para o lado: os inimigos da caçada em duas linhas (techspec-ice-dungeon).

  O toque rola nativo (overflow-x). No mouse, arrastar a faixa puxa para o lado, com a manopla
  fechada como no arraste de item; ao soltar, o scroll-snap volta a valer e encaixa na coluna.
  As setas andam uma página (a largura visível). `pagina`/`paginas` alimentam as bolinhas.
*/

const LIMIAR = 4;

export interface Carrossel {
  readonly ref: (el: HTMLDivElement | null) => void;
  readonly podeVoltar: boolean;
  readonly podeAvancar: boolean;
  readonly pagina: number;
  readonly paginas: number;
  readonly arrastando: boolean;
  passo(dir: -1 | 1): void;
  irPara(pagina: number): void;
  onPointerDown(e: ReactPointerEvent<HTMLDivElement>): void;
}

interface Estado { voltar: boolean; avancar: boolean; pagina: number; paginas: number }

const PARADO: Estado = { voltar: false, avancar: false, pagina: 0, paginas: 1 };

function gapDe(el: HTMLElement): number {
  return parseFloat(getComputedStyle(el).columnGap) || 0;
}

function medir(el: HTMLElement): Estado {
  const passo = el.clientWidth + gapDe(el);
  const max = el.scrollWidth - el.clientWidth;
  if (passo <= 0 || max <= 1) return PARADO;
  const paginas = Math.max(1, Math.ceil((el.scrollWidth + gapDe(el)) / passo));
  const fim = el.scrollLeft >= max - 1;
  return {
    voltar: el.scrollLeft > 1,
    avancar: !fim,
    pagina: fim ? paginas - 1 : Math.min(paginas - 1, Math.round(el.scrollLeft / passo)),
    paginas
  };
}

export function useCarrossel(): Carrossel {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [estado, setEstado] = useState<Estado>(PARADO);
  const [arrastando, setArrastando] = useState(false);
  const arraste = useRef<{ x: number; scroll: number; moveu: boolean } | null>(null);

  const atualizar = useCallback(() => {
    if (!el) return;
    const novo = medir(el);
    setEstado(v => (v.voltar === novo.voltar && v.avancar === novo.avancar && v.pagina === novo.pagina
      && v.paginas === novo.paginas ? v : novo));
  }, [el]);

  useEffect(() => {
    if (!el) return;
    atualizar();
    el.addEventListener('scroll', atualizar, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(atualizar);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', atualizar);
      ro?.disconnect();
    };
  }, [el, atualizar]);

  const irPara = useCallback((pagina: number) => {
    if (!el) return;
    el.scrollTo({ left: pagina * (el.clientWidth + gapDe(el)), behavior: 'smooth' });
  }, [el]);

  const passo = useCallback((dir: -1 | 1) => {
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth + gapDe(el)), behavior: 'smooth' });
  }, [el]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!el || e.pointerType !== 'mouse' || e.button !== 0 || el.scrollWidth <= el.clientWidth) return;
    arraste.current = { x: e.clientX, scroll: el.scrollLeft, moveu: false };

    const mover = (ev: PointerEvent) => {
      const a = arraste.current;
      if (!a) return;
      const dx = ev.clientX - a.x;
      if (!a.moveu && Math.abs(dx) < LIMIAR) return;
      if (!a.moveu) {
        a.moveu = true;
        setArrastando(true);
        cursorArraste('carrossel', true);
      }
      el.scrollLeft = a.scroll - dx;
    };
    const soltar = () => {
      const a = arraste.current;
      arraste.current = null;
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      if (!a?.moveu) return;
      cursorArraste('carrossel', false);
      setArrastando(false);
      // O clique que o navegador dispara no fim do arraste não é um clique.
      const engolir = (ce: MouseEvent) => { ce.stopPropagation(); ce.preventDefault(); };
      window.addEventListener('click', engolir, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', engolir, { capture: true }), 0);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  }, [el]);

  useEffect(() => () => { if (arraste.current?.moveu) cursorArraste('carrossel', false); }, []);

  return {
    ref: setEl,
    podeVoltar: estado.voltar,
    podeAvancar: estado.avancar,
    pagina: estado.pagina,
    paginas: estado.paginas,
    arrastando,
    passo,
    irPara,
    onPointerDown
  };
}
